import React, { useEffect, useRef, useState } from 'react';
import { Search, Loader2, FileText, Link2, Paperclip } from 'lucide-react';
import { useDealStore, GlobalDealSearchResult } from '../../lib/stores/dealStore';
import { useSignatureStore } from '../../lib/stores/signatureStore';
import { generateQuoteDocument } from '../../lib/pdf';
import { supabase } from '../../lib/supabase';
import { useApp } from '../AppProvider';
import { useContractClientGate } from '../../hooks/useContractClientGate';
import type { EmailAttachment } from '../../lib/stores/mail/types';
import { useTemplatesForDeal } from '../../lib/hooks/useTemplatesForDeal';
import { QuoteLinesPicker, loadQuoteLines, useQuoteLineSelection, type QuoteLine } from '../deals/modals/QuoteLinesPicker';

interface MailOfferAttachProps {
  open: boolean;
  onClose: () => void;
  onAttachPdf: (att: EmailAttachment) => void;
  onInsertLink: (html: string) => void;
  clientId?: string | null; // se il thread è collegato a un cliente → precarica le sue offerte
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result?.toString().split(',')[1] || '');
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

export function MailOfferAttach({ open, onClose, onAttachPdf, onInsertLink, clientId }: MailOfferAttachProps) {
  const { toast } = useApp();
  const searchDealsGlobal = useDealStore(s => s.searchDealsGlobal);
  const generateSignatureUrl = useSignatureStore(s => s.generateSignatureUrl);
  const contractGate = useContractClientGate();

  const [term, setTerm] = useState('');
  const [results, setResults] = useState<GlobalDealSearchResult[]>([]);
  const [clientDeals, setClientDeals] = useState<GlobalDealSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<GlobalDealSearchResult | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [working, setWorking] = useState<'pdf' | 'link' | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // Servizi e prodotti dell'offerta scelta da mettere nel PDF: tutti di default
  const [lines, setLines] = useState<QuoteLine[]>([]);
  const lineSelection = useQuoteLineSelection(lines, selected?.id);

  useEffect(() => {
    setLines([]);
    if (!selected) return;
    let cancelled = false;
    loadQuoteLines(selected.id)
      .then((rows) => { if (!cancelled) setLines(rows); })
      .catch((e) => console.warn('[MailOfferAttach] righe offerta non disponibili:', e));
    return () => { cancelled = true; };
  }, [selected?.id]);

  // I template adatti dipendono dall'offerta scelta, che qui si sceglie DOPO
  // aver aperto il pannello: il risultato di ricerca e' piatto e non porta i
  // servizi, quindi li carica l'hook quando `selected` cambia.
  const templatePerOfferta = useTemplatesForDeal(selected?.id ?? null);
  const templates = templatePerOfferta.suitable;

  // Riallinea la scelta quando cambia l'offerta: il template selezionato prima
  // potrebbe non essere adatto a quella nuova.
  useEffect(() => {
    if (templatePerOfferta.loading) return;
    const ancoraValido = templates.some(t => t.id === templateId);
    if (!ancoraValido) setTemplateId(templatePerOfferta.preferred?.id ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, templatePerOfferta.loading, templates.length]);

  useEffect(() => {
    if (!open) { setTerm(''); setResults([]); setSelected(null); setClientDeals([]); return; }
    // Precarica le offerte del cliente collegato al thread
    if (clientId) {
      (async () => {
        const { data } = await supabase
          .from('deals')
          .select('id, title, status, amount, client_id, clients(business_name)')
          .eq('client_id', clientId)
          .order('created_at', { ascending: false })
          .limit(30);
        setClientDeals((data || []).map((d: any) => ({
          id: d.id, title: d.title, status: d.status, created_at: null, amount: d.amount,
          client_id: d.client_id, client_business_name: d.clients?.business_name || null,
        })));
      })();
    }
  }, [open, clientId]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || selected) return;
    const q = term.trim();
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    const h = setTimeout(async () => {
      setResults(await searchDealsGlobal(q, 20));
      setSearching(false);
    }, 300);
    return () => clearTimeout(h);
  }, [term, open, selected, searchDealsGlobal]);

  const handlePdf = async () => {
    if (!selected || !templateId) { toast.error('Seleziona offerta e template'); return; }
    setWorking('pdf');
    try {
      const blob = await generateQuoteDocument(selected.id, templateId, 'pdf', { serviceIds: lineSelection.serviceIds });
      const content = await blobToBase64(blob);
      const safe = (selected.title || 'Offerta').replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_');
      onAttachPdf({ filename: `${safe}.pdf`, content, contentType: 'application/pdf' });
      toast.success('PDF offerta allegato');
      onClose();
    } catch (e) {
      console.error('[MailOfferAttach] pdf error:', e);
      toast.error('Errore nella generazione del PDF');
    } finally {
      setWorking(null);
    }
  };

  // GATE dati cliente: modale di verifica/conferma sempre visibile prima di
  // generare il link. Il deal viene catturato in closure perché il popover può
  // chiudersi (click fuori) mentre il modale di verifica è aperto, resettando
  // `selected`.
  const handleLink = async () => {
    if (!selected) return;
    if (!selected.client_id) { toast.error("L'offerta non ha un cliente associato"); return; }
    const deal = selected;
    await contractGate.guard(deal.client_id, () => { void doInsertLink(deal); }, {
      dealId: deal.id,
      clientName: deal.client_business_name ?? undefined,
    });
  };

  const doInsertLink = async (deal: GlobalDealSearchResult) => {
    if (!deal.client_id) return;
    setWorking('link');
    try {
      const token = await generateSignatureUrl(deal.id, deal.client_id);
      if (!token) throw new Error('no token');
      const url = `${window.location.origin}/sign/${token}`;
      const html =
        `<p><a href="${url}" target="_blank" rel="noopener noreferrer" ` +
        `style="display:inline-block;padding:10px 18px;background:#2383E2;color:#ffffff;border-radius:8px;text-decoration:none;font-weight:600;font-size:14px">` +
        `📄 Visualizza l'offerta — ${(deal.title || 'Offerta').replace(/</g, '&lt;')}</a></p>`;
      onInsertLink(html);
      toast.success('Link offerta inserito');
      onClose();
    } catch (e) {
      console.error('[MailOfferAttach] link error:', e);
      toast.error('Errore nella generazione del link');
    } finally {
      setWorking(null);
    }
  };

  // Il modale del gate vive FUORI dal blocco `open`: deve sopravvivere alla
  // chiusura del popover (click fuori) mentre l'utente verifica i dati.
  if (!open) return <>{contractGate.modal}</>;

  return (
    <>
    {contractGate.modal}
    <div ref={ref} className="absolute bottom-full left-0 mb-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-[rgba(55,53,47,0.12)] dark:border-gray-700 z-20 overflow-hidden">
      <div className="px-3 py-2 border-b border-[rgba(55,53,47,0.08)] dark:border-gray-700 flex items-center gap-2">
        <FileText className="h-4 w-4 text-[#2383E2]" />
        <span className="text-sm font-medium text-[#37352F] dark:text-white">Allega offerta</span>
      </div>

      {!selected ? (
        <div className="p-3 space-y-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#787774]" />
            <input
              autoFocus
              value={term}
              onChange={e => setTerm(e.target.value)}
              placeholder="Cerca offerta per titolo o cliente..."
              className="w-full pl-8 pr-3 py-1.5 text-sm bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-md focus:outline-none focus:ring-1 focus:ring-[#2383E2] text-[#37352F] dark:text-white"
            />
          </div>
          {searching && <div className="flex items-center gap-2 text-xs text-[#787774] px-1"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Ricerca…</div>}
          {(() => {
            const isSearch = term.trim().length >= 2;
            const shown = isSearch ? results : clientDeals;
            return (
              <>
                {!isSearch && clientDeals.length > 0 && (
                  <p className="text-[11px] text-[#787774] dark:text-white/40 px-1">Offerte di {clientDeals[0].client_business_name || 'questo contatto'}</p>
                )}
                <div className="max-h-56 overflow-y-auto space-y-0.5">
                  {shown.map(d => (
                    <button key={d.id} onClick={() => setSelected(d)} className="w-full text-left px-2.5 py-2 rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5">
                      <p className="text-sm font-medium text-[#37352F] dark:text-white truncate">{d.title || 'Offerta senza titolo'}</p>
                      {d.client_business_name && <p className="text-xs text-[#787774] dark:text-white/40 truncate">{d.client_business_name}</p>}
                    </button>
                  ))}
                  {!isSearch && clientDeals.length === 0 && (
                    <p className="text-xs text-[#787774] dark:text-white/40 px-1 py-2">Digita per cercare un'offerta…</p>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      ) : (
        <div className="p-3 space-y-3">
          <div className="flex items-start gap-2">
            <FileText className="h-4 w-4 text-[#787774] mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-[#37352F] dark:text-white truncate">{selected.title || 'Offerta'}</p>
              {selected.client_business_name && <p className="text-xs text-[#787774] dark:text-white/40 truncate">{selected.client_business_name}</p>}
            </div>
          </div>

          {templates.length > 1 && (
            <div>
              <label className="text-[11px] text-[#787774] dark:text-white/40 mb-1 block">Template PDF</label>
              <select value={templateId} onChange={e => setTemplateId(e.target.value)}
                className="w-full px-2.5 py-1.5 text-sm bg-[rgba(55,53,47,0.04)] dark:bg-white/5 border border-[rgba(55,53,47,0.09)] dark:border-gray-700 rounded-md focus:outline-none focus:ring-1 focus:ring-[#2383E2] text-[#37352F] dark:text-white">
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}

          <QuoteLinesPicker
            lines={lines}
            selected={lineSelection.selected}
            onChange={lineSelection.setSelected}
            compact
          />

          <div className="flex items-center gap-2">
            <button onClick={handlePdf} disabled={!!working || templates.length === 0 || lineSelection.noneSelected}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-[#2383E2] text-white text-xs font-medium rounded-md hover:bg-[#1a6fc9] disabled:opacity-50">
              {working === 'pdf' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
              Allega PDF
            </button>
            <button onClick={handleLink} disabled={!!working}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-[rgba(55,53,47,0.06)] dark:bg-white/10 text-[#37352F] dark:text-white text-xs font-medium rounded-md hover:bg-[rgba(55,53,47,0.1)] disabled:opacity-50">
              {working === 'link' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
              Inserisci link
            </button>
          </div>
          {templates.length === 0 && !templatePerOfferta.loading && (
            <p className="text-[11px] text-[#D9730D]">
              {templatePerOfferta.unsuitable.length > 0
                ? 'Nessun template adatto ai servizi di questa offerta.'
                : 'Nessun template preventivo: crea un template per allegare il PDF.'}
            </p>
          )}
          <button onClick={() => setSelected(null)} className="text-xs text-[#787774] hover:text-[#37352F] dark:hover:text-white">← Cambia offerta</button>
        </div>
      )}
    </div>
    </>
  );
}
